// Phase MHM-2 · Ops Live Map — 6 toggleable Leaflet layers.
import { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, Marker, Popup, CircleMarker, Polygon, Polyline } from "react-leaflet";
import ThemedTileLayer from "../../components/hub/ThemedTileLayer";
import L from "leaflet";
import { RefreshCw, Loader2 } from "lucide-react";
import api from "../../services/api";
import { toast } from "sonner";
import MHMSubNav from "../../components/hub/MHMSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import SlaAlertBadge from "../../components/hub/SlaAlertBadge";
import RoleGate from "../../components/RoleGate";

const agentIcon = L.divIcon({ className: "", html: `<span style="background:#2563eb;color:white;border-radius:50%;width:18px;height:18px;display:inline-block;border:2px solid white;box-shadow:0 0 0 2px #2563eb;"></span>`, iconSize: [18, 18] });
const deliveryIcon = L.divIcon({ className: "", html: `<span style="background:#a855f7;color:white;border-radius:50%;width:14px;height:14px;display:inline-block;border:2px solid white;"></span>`, iconSize: [14, 14] });
const microIcon = L.divIcon({ className: "", html: `<span style="background:#22c55e;color:white;border-radius:4px;width:18px;height:18px;display:inline-block;border:2px solid white;"></span>`, iconSize: [18, 18] });

const COURIER_COLORS = { delhivery: "#0ea5e9", ekart: "#a855f7", bluedart: "#2563eb", dtdc: "#f97316", own_fleet: "#22c55e" };
const DEFAULTS = { agents: true, deliveries: true, pickup_density: true, delayed_zones: true, micro_hubs: true, dispatch_routes: false };

export default function OpsLiveMap() {
  const [snap, setSnap] = useState(null);
  const [layers, setLayers] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [lastRefreshAge, setLastRefreshAge] = useState(0);
  const lastFetched = useRef(Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub/live-map/snapshot");
      setSnap(data);
      lastFetched.current = Date.now();
      setLastRefreshAge(0);
    } catch (e) { toast.error(e.userMessage || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const a = setInterval(() => setLastRefreshAge(Math.round((Date.now() - lastFetched.current) / 1000)), 1000);
    return () => { clearInterval(t); clearInterval(a); };
  }, [load]);

  const toggle = (k) => setLayers((s) => ({ ...s, [k]: !s[k] }));

  const center = (snap?.agents?.[0])
    ? [snap.agents[0].lat, snap.agents[0].lng]
    : (snap?.micro_hubs?.[0])
      ? [snap.micro_hubs[0].lat || 12.97, snap.micro_hubs[0].lng || 77.59]
      : [12.9716, 77.5946];

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 space-y-3" data-testid="mhm-livemap-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-livemap-title">
              Ops Live Map
            </h1>
            <p className="text-sm text-slate-500" data-testid="mhm-livemap-refresh-age">
              Hub: {snap?.hub_id || "—"} · last refreshed {lastRefreshAge}s ago
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SlaAlertBadge />
            <button onClick={load} data-testid="mhm-livemap-refresh-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
          </div>
        </div>

        <div className="relative">
          {/* Legend / layer toggles */}
          <div className="absolute right-3 top-3 z-[400] bg-white border border-slate-200 rounded-xl p-3 shadow-md text-xs space-y-1"
               data-testid="mhm-livemap-legend">
            {Object.keys(DEFAULTS).map((k) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={layers[k]} onChange={() => toggle(k)}
                       data-testid={`mhm-livemap-toggle-${k}`}/>
                <span className="capitalize">{k.replace(/_/g, " ")}</span>
                <span className="ml-auto text-slate-400">
                  ({(snap?.[k] || []).length})
                </span>
              </label>
            ))}
          </div>
          <div className="h-[68vh] rounded-2xl overflow-hidden border border-slate-200" data-testid="mhm-livemap">
            <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <ThemedTileLayer />
              {layers.agents && (snap?.agents || []).map((a) => (
                <Marker key={`a-${a.agent_user_id}`} position={[a.lat, a.lng]} icon={agentIcon}>
                  <Popup><div className="text-xs"><b>{a.name}</b><br/>{a.fresh ? "Live" : "Stale"}</div></Popup>
                </Marker>
              ))}
              {layers.deliveries && (snap?.deliveries || []).map((d) => (
                <Marker key={`d-${d.tracking_id}`} position={[d.drop_lat, d.drop_lng]} icon={deliveryIcon}>
                  <Popup>
                    <div className="text-xs">
                      <TrackingIdLink tid={d.tracking_id} className="font-bold" /><br/>{d.status}<br/>{d.drop_city || "—"}
                    </div>
                  </Popup>
                </Marker>
              ))}
              {layers.pickup_density && (snap?.pickup_density || []).map((p) => (
                <CircleMarker key={`p-${p.pincode}`} center={[p.lat, p.lng]}
                              radius={Math.min(20, Math.max(6, p.count * 3))}
                              pathOptions={{ color: "#f97316", fillColor: "#fdba74", fillOpacity: 0.4 }}>
                  <Popup><div className="text-xs">Pincode {p.pincode}<br/>{p.count} pending pickup{p.count > 1 ? "s" : ""}</div></Popup>
                </CircleMarker>
              ))}
              {layers.delayed_zones && (snap?.delayed_zones || []).filter((z) => z.lat != null).map((z) => (
                <CircleMarker key={`z-${z.pin_prefix}`} center={[z.lat, z.lng]} radius={28}
                              pathOptions={{ color: "#dc2626", fillColor: "#fecaca", fillOpacity: 0.35 }}>
                  <Popup><div className="text-xs">Pin {z.pin_prefix}xx<br/><b>{z.count}</b> stuck shipments</div></Popup>
                </CircleMarker>
              ))}
              {layers.micro_hubs && (snap?.micro_hubs || []).filter((h) => h.lat != null).map((h) => (
                <Marker key={`h-${h.hub_id}`} position={[h.lat, h.lng]} icon={microIcon}>
                  <Popup><div className="text-xs"><b>{h.name}</b><br/>{h.hub_id}<br/>{h.is_active ? "Active" : "Inactive"}</div></Popup>
                </Marker>
              ))}
              {layers.dispatch_routes && (snap?.dispatch_routes || []).map((r) => (
                <Polyline key={r.manifest_id} positions={r.polyline}
                          pathOptions={{ color: COURIER_COLORS[r.partner] || "#64748b", weight: 3, opacity: 0.7 }}>
                  <Popup><div className="text-xs"><b>{r.manifest_id}</b><br/>{r.partner}</div></Popup>
                </Polyline>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
    </RoleGate>
  );
}
